/**
 * @module aem/components/product-offerings
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable:no-any */ // Need to support all data types
import { Injectable, EventEmitter } from '@angular/core';
import { ApiGatewayService } from '@core/public';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { ProductDetail, Product, ItemDetail } from '@support/api/common/product.interface';
import { ProductDetails } from '@experience/shop/product-details/interfaces/product-details.interface';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { ProductSubTypeEnum } from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
import { PRODUCT_DETAILS_CONST } from '../product-details/product-details.constants';
import { APIError } from '@support/api/common/error.interface';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { PRODUCT_OFFERINGS_CONSTANTS } from '@core/aem/components/product-offerings/product-offerings.constants';
import { Cart, CartItem } from '@support/api/proxies/responses/api-cart-response.interface';
import { CONTENT_ENDPOINT } from '@core/aem/content-endpoints';
import { SETTINGS } from '@environment/settings';
import { ProductDetailsRequestModel } from '../product-details/models/product-details-request.model';
import { ProductDetailsDefaultModel } from '@experience/shop/shared-browse/models/productDetails-default.model';
import {
  ProductReviewInterface,
  ReviewFeedback,
  ReviewFeedBackResp,
} from '@core/aem/components/product-reviews/interface/product-review.interface';
import { ReviewsQuery } from '../product-details/models/review-query.model';
import { ApiCache } from '@support/shared/interfaces/api-cache-object.interface';
import { throwError as observableThrowError, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AddSkuItemsToNonLineInterface } from '@support/api/proxies/requests/api-2107.interface';
import { HttpResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ProductDetailsService {
  public pricingOption: string;
  public getselectedDeviceId$: EventEmitter<ItemDetail>;

  constructor(
    private apiGateway: ApiGatewayService,
    private cartSupportService: CartSupportService,
    private contentProviderService: ContentProviderService,
    private objectService: ObjectService,
    private stateUtilService: StateStoreUtilsService
  ) {
    this.getselectedDeviceId$ = new EventEmitter();
  }

  public getProductDetails(
    id: string,
    type: string,
    hasCPO?: boolean,
    isCacheAvailable?: string
  ): Observable<ProductDetail> {
    const retrieveStoreId: string = this.stateUtilService.getStoreId();
    const pathParams = {
      canonicalUrl: id,
      isProductAvailable: isCacheAvailable,
    };
    const cacheObj: ApiCache = {
      cacheKey: JSON.stringify(pathParams).concat(this.stateUtilService.getInteractionId()),
      cacheResponse: true,
      preferCache: true,
    };
    const customerConfigData = this.stateUtilService.getCustomerConfigData();
    const billingZip =
      this.objectService.get(this.stateUtilService.getBillingAddress(), 'postalCode') ||
      this.objectService.get(customerConfigData, 'zip');
    const customerId = this.stateUtilService.getCustomerId();
    const accountId = this.stateUtilService.getAccountId();
    const pdpRequest = new ProductDetailsRequestModel(
      SETTINGS.CHANNEL,
      type,
      this.stateUtilService.getBillingTypeOfCustomerInFocus(),
      retrieveStoreId,
      billingZip,
      customerId,
      accountId,
      hasCPO
    );
    return this.apiGateway
      .postRequest(
        API_MAP.API_2101_retrieveProductDetails,
        pdpRequest,
        null,
        null,
        pathParams,
        cacheObj
      )
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * getCachedProductDetails function
   * @param id offerId to identify sku
   */
  public getCachedProductDetails(id: string): Product {
    return this.stateUtilService.getProductDetails(id);
  }

  /**
   * gets the pdp static content
   * @returns Observable<object>
   */
  public getProductDetailsContent(): Observable<object> {
    return this.contentProviderService
      .getContentData(CONTENT_ENDPOINT.PRODUCT_DETAILS, 'shared', true)
      .pipe(
        map((contentData: ConfigData) => {
          return this.contentProviderService.getConfigurableData(contentData);
        })
      );
  }

  /**
   * TODO: Remove when fixed to not call CPS multiple times.
   * This is temp fix to unblock SEO
   * @returns Observable<object>
   */
  public getProductDetailsAuthorableContent(): Observable<object> {
    return this.contentProviderService
      .getAuthorableComponentData(CONTENT_ENDPOINT.PRODUCT_DETAILS, 'shared', true)
      .pipe(
        map((contentData: ConfigData) => {
          return this.contentProviderService.getConfigurableData(contentData);
        })
      );
  }

  /**
   * gets the reviews landing static content
   * @returns Observable<object>
   */
  public getProductReviewsDetailsContent(): Observable<object> {
    return this.contentProviderService.getContentData(CONTENT_ENDPOINT.PRODUCT_REVIEWS).pipe(
      map((contentData: ConfigData) => {
        return this.contentProviderService.getConfigurableData(contentData);
      })
    );
  }

  /**
   * Gets Title that needs to be displayed on PDP
   * @param tabletDetailsTitle : Title for Tablet's PDP coming from AEM
   * @param productDetailsTitle : Title for Product's PDP coming from AEM
   * @param accessoriesDetailsTitle : Title for Accessories's PDP coming from AEM
   * @returns string
   */
  public getProductDetailsTitle(
    productDetails: ProductDetailsDefaultModel,
    tabletDetailsTitle: string,
    productDetailsTitle: string,
    accessoriesDetailsTitle: string
  ): string {
    if (this.objectService.get(productDetails, 'productSubType')) {
      if (
        this.objectService.get(productDetails, 'productSubType').toUpperCase() ===
          ProductSubTypeEnum[0] ||
        this.objectService.get(productDetails, 'productSubType').toUpperCase() ===
          ProductSubTypeEnum[PRODUCT_OFFERINGS_CONSTANTS.TWO]
      ) {
        return productDetailsTitle;
      } else if (
        this.objectService.get(productDetails, 'productSubType').toUpperCase() ===
        ProductSubTypeEnum[1]
      ) {
        return productDetailsTitle;
      } else {
        return accessoriesDetailsTitle;
      }
    } else {
      return accessoriesDetailsTitle;
    }
  }

  /**
   * adds accessory to cart or updates the quantity if the accessory already exists
   * @param isExistingAccessory
   * @param selectedQty
   * @param sku
   * @param selectedEipAmount
   * @param cartItemId (optional)
   * @param overrideIndicator (optional)
   * @returns updated cart response
   */
  public addAccessoryToCart(
    skuCollection: AddSkuItemsToNonLineInterface[],
    overrideIndicator?: boolean
  ): Observable<Cart> {
    return this.cartSupportService.postAccessoryToCart(skuCollection, overrideIndicator);
  }

  /**
   * gets the quantity of the existing in the cart, if it does not exists, it returns 0
   * @param deviceDetails
   * @returns quantity count
   */
  public getExistingAccessoryCount(deviceDetails: ProductDetails): number {
    const sku =
      deviceDetails[deviceDetails.defaultCondition][deviceDetails.defaultSelection].skuCode;
    const currentCart: Cart = this.cartSupportService.getStatestoreCartReference();
    if (currentCart) {
      const accessoriesArr = currentCart.accessories.filter((accessory: CartItem) => {
        return accessory.upc === sku;
      });
      return accessoriesArr.length && accessoriesArr[0].quantity;
    } else {
      return 0;
    }
  }

  /**
   * Method to set pricing options
   * @param option dropdown values
   */
  public setPricingOption(option: string): void {
    this.pricingOption =
      option === PRODUCT_OFFERINGS_CONSTANTS.PAY_IN_FULL
        ? PRODUCT_OFFERINGS_CONSTANTS.FRP
        : PRODUCT_OFFERINGS_CONSTANTS.EIP;
  }

  /**
   * Method used to check is it warranty replacement flow
   * @returns boolean
   */
  public isWarrantyReplacementFlow(): boolean {
    const type = this.stateUtilService.getDeviceAssessmentType();
    this.stateUtilService.setDeviceAssessmentType('');
    return type === PRODUCT_DETAILS_CONST.WARRANTY;
  }

  public getDeviceFromCart(): CartItem {
    const cartData = this.stateUtilService.getCart();
    const selectedLine = this.stateUtilService.getLineIdForTileIdInFocus();
    if (cartData) {
      const cartLine = this.cartSupportService.getLineFromCart(selectedLine);
      if (cartLine && cartLine.device && cartLine.device.offerId) {
        return cartLine.device;
      } else if (!cartLine && cartData.devices) {
        const tileInFocus = this.stateUtilService.getTileIdInFocus();
        const tileModel = this.stateUtilService.getTileModelForTileId(tileInFocus);
        const nonLineDeviceId = this.objectService.get(tileModel, 'deviceId');
        const nonLineData = cartData.devices.filter((device: CartItem) => {
          return device.id === nonLineDeviceId;
        })[0];
        return nonLineData;
      } else {
        return null;
      }
    }
  }

  /**
   * Method to get Device Reviews details from API 2145
   * @param product Id and limit to show
   * @returns ProductReviewInterface
   */
  public getDeviceReviews(
    productId: string,
    queryParam: ReviewsQuery
  ): Observable<ProductReviewInterface> {
    const pathParams = {
      productId: productId,
      isProductAvailable: true,
    };
    const cacheObj: ApiCache = {
      cacheKey: JSON.stringify(pathParams).concat(this.stateUtilService.getInteractionId()),
      cacheResponse: true,
      preferCache: true,
    };
    const queryParams = queryParam.queryParams;
    // Added request changes for get to post api conversion
    const payload = {
      productId: productId,
      type: queryParams.type,
      offset: queryParams.offset,
      filter: queryParams.filter,
      limit: queryParams.limit,
      search: queryParams.search,
      sortBy: queryParams.sortBy,
    };
    return this.apiGateway
      .postRequest(API_MAP.API_2145_getProductReviews, payload, null, null, null, cacheObj)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Method to get Device Reviews details from API 2145
   * @param product Id and limit to show
   * @returns ProductReviewInterface
   */
  public postReviewFeedback(
    productId: string,
    requestPayload: ReviewFeedback[]
  ): Observable<ReviewFeedBackResp[]> {
    const pathParams = {
      productId: productId,
    };
    return this.apiGateway
      .postRequest(
        API_MAP.API_2146_postProductReviewFeedback,
        requestPayload,
        null,
        null,
        pathParams
      )
      .pipe(
        map((response: HttpResponse<ReviewFeedBackResp[]>) => {
          return response.body;
        })
      );
  }
  /**
   * Method to get product details for ad banner from API 2101
   * @param bannerAdDesc
   * @returns Product
   */
  public getProductDetailsForAdBanner(
    bannerAdDesc: string,
    globalSearchURL?: string
  ): Observable<Product> {
    const TWO = 2;
    const THREE = 3;
    const canonicalUrl = globalSearchURL ? globalSearchURL : bannerAdDesc.split('/')[TWO];
    const selectedColor = bannerAdDesc.split('/')[THREE]
      ? bannerAdDesc.split('/')[THREE].split('.')[0]
      : '';
    this.stateUtilService.setSelectedColor(selectedColor);
    return this.getProductDetails(canonicalUrl, 'Device', null, 'true').pipe(
      map((response: ProductDetail) => {
        if (this.objectService.get(response, 'model-view')) {
          const productData: Product = this.objectService.get(
            response,
            'model-view.model.model-data.product-offerings.productDetailsResponse'
          );
          const productItems: Product = productData || null;
          const productCanonicalUrl = this.objectService.get(productData, 'familyId');
          this.stateUtilService.setSelectedOfferId(productCanonicalUrl);
          const productfamilyName = this.objectService.get(productData, 'familyName');
          this.stateUtilService.setSelectedDeviceFamilyName(productfamilyName);
          return productItems;
        }
      })
    );
  }

  public publishMetaBeforeContent(): void {
    this.contentProviderService.publishMetaBeforeContent();
  }
}
